import { useEffect, useRef } from 'react'
import { useDagTableStore } from '@/stores/useDagTableStore'
import { toast } from '@/stores/useToastStore'
import { isCellEditable } from './DagCellRenderer'
import {
  HIERARCHY_COLUMN_IDS,
  HIERARCHY_LEVEL_MAP,
} from './customColumnRegistry'
import { getHierarchyOptionsForLevel } from './hierarchyDropdownUtils'
import type { DagColumnMeta, DropdownOptionRead, DagRowRead } from '@/types/dag'
import type { HierarchyForDagData } from '@/hooks/useHierarchyForDag'

const normalizeText = (value: string) =>
  value.trim().replace(/\s+/g, ' ').toLowerCase()

const findDropdownMatch = (
  options: DropdownOptionRead[],
  pastedText: string
) => {
  const normalized = normalizeText(pastedText)
  return options.find(
    o =>
      normalizeText(o.value) === normalized ||
      normalizeText(o.display_label ?? '') === normalized
  )
}

const isEditableTextTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false
  const tagName = target.tagName
  return (
    tagName === 'INPUT' ||
    tagName === 'TEXTAREA' ||
    target.isContentEditable ||
    !!target.closest('[contenteditable="true"]')
  )
}

const buildHierarchyCascadeUpdates = (colId: string, value: string) => {
  if (colId === 'division') {
    return [
      { columnId: 'division', value },
      { columnId: 'department', value: null },
      { columnId: 'class', value: null },
      { columnId: 'subclass', value: null },
    ]
  }

  if (colId === 'department') {
    return [
      { columnId: 'department', value },
      { columnId: 'class', value: null },
      { columnId: 'subclass', value: null },
    ]
  }

  if (colId === 'class') {
    return [
      { columnId: 'class', value },
      { columnId: 'subclass', value: null },
    ]
  }

  return [{ columnId: 'subclass', value }]
}

interface UseDagDropdownPasteOptions {
  isReadOnly?: boolean
  columnsMap: Map<string, DagColumnMeta>
  rows: DagRowRead[]
  dropdownMap: Record<string, DropdownOptionRead[]>
  hierarchyData: HierarchyForDagData | null | undefined
  getRowVersion: (rowId: string) => number
  handleCellEdit: (
    rowId: string,
    columnId: string,
    value: unknown,
    rowVersion: number
  ) => void
  handleMultiCellEdit: (
    rowId: string,
    cells: Array<{ columnId: string; value: unknown }>,
    rowVersion: number
  ) => void
}

export function useDagDropdownPaste({
  isReadOnly,
  columnsMap,
  rows,
  dropdownMap,
  hierarchyData,
  getRowVersion,
  handleCellEdit,
  handleMultiCellEdit,
}: UseDagDropdownPasteOptions) {
  // Keep a ref to always read the latest values without stale closures
  const optsRef = useRef({
    isReadOnly,
    columnsMap,
    rows,
    dropdownMap,
    hierarchyData,
    getRowVersion,
    handleCellEdit,
    handleMultiCellEdit,
  })
  optsRef.current = {
    isReadOnly,
    columnsMap,
    rows,
    dropdownMap,
    hierarchyData,
    getRowVersion,
    handleCellEdit,
    handleMultiCellEdit,
  }

  useEffect(() => {
    const handleCopyShortcut = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'c') return

      // Let normal text selection copy work.
      if (window.getSelection()?.toString().trim()) return

      const { focusedCell, editingCell } = useDagTableStore.getState()
      if (!focusedCell) return

      const isFocusedBeingEdited =
        !!editingCell &&
        editingCell.rowId === focusedCell.rowId &&
        editingCell.colId === focusedCell.colId
      if (isFocusedBeingEdited) return

      const {
        columnsMap: cols,
        rows: rowList,
        dropdownMap: dMap,
        hierarchyData: hData,
      } = optsRef.current

      const { rowId, colId } = focusedCell
      const colMeta = cols.get(colId)
      if (!colMeta) return

      const isDropdownLike =
        colMeta.dropdown_category ||
        colMeta.format === 'boolean' ||
        HIERARCHY_COLUMN_IDS.has(colId)
      if (!isDropdownLike) return

      const row = rowList.find(r => r.id === rowId)
      if (!row) return

      const rawValue = row.cells?.[colId]
      if (rawValue == null || rawValue === '') return

      let copyValue = ''

      if (colMeta.format === 'boolean') {
        copyValue = rawValue === true || rawValue === 'true' ? 'Yes' : 'No'
      } else if (HIERARCHY_COLUMN_IDS.has(colId) && hData) {
        const options = getHierarchyOptionsForLevel(
          HIERARCHY_LEVEL_MAP[colId],
          row.cells ?? {},
          hData
        )
        const valueAsString = String(rawValue)
        const match = options.find(o => o.value === valueAsString)
        copyValue = match?.display_label ?? valueAsString
      } else if (colMeta.dropdown_category) {
        const options =
          dMap[colMeta.dropdown_category] ??
          colMeta.options?.map(o => ({ value: o })) ??
          []
        const valueAsString = String(rawValue)
        const match = options.find(o => o.value === valueAsString)
        copyValue = match?.display_label ?? valueAsString
      }

      if (!copyValue) return
      e.preventDefault()
      navigator.clipboard.writeText(copyValue).catch(() => {})
    }

    const handlePaste = (e: ClipboardEvent) => {
      const { focusedCell, editingCell } = useDagTableStore.getState()

      // No focused cell — nothing to do
      if (!focusedCell) return

      const isFocusedBeingEdited =
        !!editingCell &&
        editingCell.rowId === focusedCell.rowId &&
        editingCell.colId === focusedCell.colId

      // If focused cell is actively editing, let in-cell input onPaste handle it.
      if (isFocusedBeingEdited) return

      // If user is typing in another text input, do not hijack paste.
      if (isEditableTextTarget(e.target)) return

      const {
        isReadOnly: ro,
        columnsMap: cols,
        rows: rowList,
        dropdownMap: dMap,
        hierarchyData: hData,
        getRowVersion: getVer,
        handleCellEdit: editCell,
        handleMultiCellEdit: editMultiCell,
      } = optsRef.current

      if (ro) return

      const { rowId, colId } = focusedCell
      const colMeta = cols.get(colId)
      if (!colMeta) return

      // Only intercept dropdown-like columns; let native behaviour handle the rest
      const isDropdown =
        colMeta.dropdown_category ||
        colMeta.format === 'boolean' ||
        HIERARCHY_COLUMN_IDS.has(colId)
      if (!isDropdown) return

      const row = rowList.find(r => r.id === rowId)
      if (!row) return

      // Skip non-editable cells
      if (!isCellEditable(colMeta, row.cells ?? {})) return

      const pastedText = e.clipboardData?.getData('text')?.trim()
      if (!pastedText) return

      e.preventDefault()

      const rowVersion = getVer(rowId)

      // --- Boolean ---
      if (colMeta.format === 'boolean') {
        const normalizedPaste = normalizeText(pastedText)
        if (normalizedPaste === 'yes' || normalizedPaste === 'true') {
          editCell(rowId, colId, true, rowVersion)
        } else if (normalizedPaste === 'no' || normalizedPaste === 'false') {
          editCell(rowId, colId, false, rowVersion)
        } else {
          toast.error(
            `"${pastedText}" is not a valid option. Use "Yes" or "No".`
          )
        }
        return
      }

      // --- Hierarchy dropdowns ---
      if (HIERARCHY_COLUMN_IDS.has(colId) && hData) {
        const options = getHierarchyOptionsForLevel(
          HIERARCHY_LEVEL_MAP[colId],
          row.cells ?? {},
          hData
        )
        const match = findDropdownMatch(options, pastedText)
        if (match) {
          editMultiCell(
            rowId,
            buildHierarchyCascadeUpdates(colId, match.value),
            rowVersion
          )
        } else {
          toast.error(`"${pastedText}" is not a valid option for this field`)
        }
        return
      }

      // --- Regular dropdown_category ---
      if (colMeta.dropdown_category) {
        const options =
          dMap[colMeta.dropdown_category] ??
          colMeta.options?.map(o => ({ value: o })) ??
          []
        const match = findDropdownMatch(options, pastedText)
        if (match) {
          editCell(rowId, colId, match.value, rowVersion)
        } else if (colMeta.dropdown_mode === 'EXTENSIBLE') {
          editCell(rowId, colId, pastedText, rowVersion)
        } else {
          toast.error(`"${pastedText}" is not a valid option for this field`)
        }
      }
    }

    document.addEventListener('keydown', handleCopyShortcut)
    document.addEventListener('paste', handlePaste)
    return () => {
      document.removeEventListener('keydown', handleCopyShortcut)
      document.removeEventListener('paste', handlePaste)
    }
  }, [])
}
